import React, { useState, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import * as ROUTES from '../constants/routes';
import { fetchCelebrity } from '../actions';
import { getCelebrity, getFields } from '../reducers';
import { useRouter, useSearch } from '../hooks';
import CelebrityForm from './Celebrity/CelebrityForm';
import CelebrityImage from './Celebrity/CelebrityImage';
import withEmailVerification from './withEmailVerification';
import { useTranslation } from 'react-i18next';
import { translateFieldLabel } from '../utils/fields';

function CelebritySuggest() {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(0);
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const { setQuery, hits, loading, error } = useSearch('');
  useEffect(() => {
    setQuery(firstname + (lastname ? ' ' + lastname : ''));
  }, [firstname, lastname, setQuery]);

  const { match } = useRouter();
  const celebrityId = match.params.id ? match.params.id : null;

  const [loadingCelebrity, setLoadingCelebrity] = useState(!celebrityId);
  const dispatch = useDispatch();
  useEffect(() => {
    if (celebrityId) {
      dispatch(fetchCelebrity(celebrityId)).then(() => {
        setLoadingCelebrity(false);
      });
    }
  }, [celebrityId, dispatch]);

  const celebrity = useSelector(state => getCelebrity(state, celebrityId));

  const fields = useSelector(getFields);

  if (celebrityId) {
    return (
      <>
        {loadingCelebrity ? (
          <div className="container" dir={i18n.language === 'ar' ? 'ltr' : 'auto'}>
            <div className="row mb-3">
              <div className="col-12 text-center">
                <div
                  className="spinner-grow spinner-grow-lg text-primary"
                  role="status"
                >
                  <span className="sr-only">{t('common.loading')}</span>
                </div>
              </div>
            </div>
          </div>
        ) : !celebrity ? (
          <div className="container" dir={i18n.language === 'ar' ? 'ltr' : 'auto'}>
            <div className="row mb-3">
              <div className="col-12 text-center">
                <span className="text-primary">{t('celebrity.notFound')}</span>
              </div>
            </div>
          </div>
        ) : (
          <CelebrityForm
            initialCelebrity={celebrity}
            celebrityId={celebrityId}
          />
        )}
      </>
    );
  }

  const initialCelebrity = {
    firstname,
    lastname,
  };

  function handleSubmit(event) {
    event.preventDefault();
    setStep(1);
  }

  function handleSubmitSuccess() {
    setFirstname('');
    setLastname('');
    setStep(0);
  }

  if (step === 0) {
    return (
      <div className="container" dir={i18n.language === 'ar' ? 'ltr' : 'auto'}>
        <h1 style={{ color: '#0A2342' }}>{t('celebrity.suggest.title')}</h1>
        <form
          className="form-inline"
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          <label className="sr-only" htmlFor="firstname">
            {t('celebrity.suggest.firstnameLabel')}
          </label>
          <input
            className="form-control mb-2 mr-sm-2"
            id="firstname"
            type="text"
            name="firstname"
            placeholder={t('celebrity.suggest.firstnamePlaceholder')}
            value={firstname}
            onChange={e => setFirstname(e.target.value)}
            required
          />
          <label className="sr-only" htmlFor="lastname">
            {t('celebrity.suggest.lastnameLabel')}
          </label>
          <input
            className="form-control mb-2 mr-sm-2"
            id="lastname"
            type="text"
            name="lastname"
            placeholder={t('celebrity.suggest.lastnamePlaceholder')}
            value={lastname}
            onChange={e => setLastname(e.target.value)}
          />
          <button className="btn btn-primary mb-2" type="submit">
            {t('celebrity.suggest.next')}
          </button>
        </form>

        <div
          className={`list-group already-suggested-container${
            hits !== null ? ' d-flex' : ' d-none'
          }`}
        >
          {loading ? (
            <div className="list-group-item d-flex justify-content-center">
              <div
                className="spinner-grow spinner-grow-sm text-primary"
                role="status"
              >
                <span className="sr-only">{t('common.loading')}</span>
              </div>
            </div>
          ) : (
            error && (
              <div className="list-group-item">
                <span className="text-danger">{error.message}</span>
              </div>
            )
          )}
          {hits && (
            <>
              <div className="list-group-item p-1 pl-3 text-dark">
                <small>
                  {t('celebrity.suggest.searchByPrefix')}{' '}
                  <a
                    className="text-dark"
                    href="https://www.algolia.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Algolia
                  </a>
                </small>
              </div>
              <div className="search-results-container overflow-auto mb-2" style={{ maxHeight: '300px' }}>
                {hits.map((celebrity, idx) => (
                  <SearchResultItem
                    key={celebrity.objectID}
                    celebrity={celebrity}
                    fields={fields}
                    last={idx === hits.length - 1}
                  />
                ))}
              </div>
            </>
          )}
        </div>
        <CelebritySuggestInfo />
      </div>
    );
  }

  return (
    <>
      <CelebrityForm
        title={t('celebrity.suggest.title')}
        initialCelebrity={initialCelebrity}
        onSubmitSuccess={handleSubmitSuccess}
      />
      <CelebritySuggestInfo />
    </>
  );
}

export default withEmailVerification(CelebritySuggest);
//export default CelebritySuggest;

function SearchResultItem({ celebrity, fields, last }) {
  const { t } = useTranslation();
  const imageContainerRef = useRef(null);

  return (
    <div
      className={`list-group-item rounded-0${last ? ' border-bottom-0' : ''}`}
    >
      <div className="container">
        <div className="row">
          <div className="col-3 px-0" ref={imageContainerRef}>
            <CelebrityImage
              celebrity={celebrity}
              container={imageContainerRef.current}
            />
          </div>
          <div className="col-9 pr-0">
            <div>
              {celebrity.firstname} {celebrity.lastname}
            </div>
            {celebrity.fields.length > 0 && (
              <div style={{ fontSize: '0.75rem' }}>
                {celebrity.fields.reduce(
                  (acc, field, idx) =>
                    acc +
                    translateFieldLabel(fields, field, t) +
                    (idx !== celebrity.fields.length - 1 ? ', ' : ''),
                  ''
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function CelebritySuggestLink() {
  const { t, i18n } = useTranslation();
  return (
    <div dir={i18n.language === 'ar' ? 'ltr' : 'auto'}>
      <Link to={ROUTES.CELEBRITY_SUGGEST}>{t('celebrity.suggest.link')}</Link>
    </div>
  );
}

export function CelebritySuggestButton() {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  function handleClick() {
    history.push(ROUTES.CELEBRITY_SUGGEST);
  }

  return (
    <form className="form-inline" dir={i18n.language === 'ar' ? 'ltr' : 'auto'}>
      <button className="btn btn-primary" type="button" onClick={handleClick}>
        {t('celebrity.suggest.button')}
      </button>
    </form>
  );
}

function CelebritySuggestInfo() {
  const { t } = useTranslation();
  const sections = t('info.aboutPage.sections', { returnObjects: true }) || [];
  const suggestSections = [2, 3, 4]
    .map(index => sections[index])
    .filter(Boolean);

  return (
    <div className="jumbotron jumbotron-fluid mb-3 mt-4">
      <div className="container">
        {suggestSections.map((section, index) => (
          <div key={`${section.title}-${index}`}>
            <h2 style={{ color: '#0A2342' }}>{section.title}</h2>
            {section.paragraphs.map((paragraph, pIdx) => (
              <p className="lead" key={`${section.title}-${pIdx}`}>
                {paragraph}
              </p>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
